import { useRef, useContext, useEffect } from 'react'

import CodeBlock from '../../../study/components/CodeBlock'
import { TelemetryContext } from '../context/TelemetryContext'
import { useRegion } from '../hooks/useRegion'

import { PROVIDED_EVENTS, REGIONS } from '../constants/telemetryEvents'

import '../css/workspace.scss'

// An exercise's provided code (test_code.provided: E3's Person class), under the task: shown
// read-only, never in the editor - every run and every test loads it before the student's code
// (testResults.withProvidedCode), so the student uses it without copying it. Highlighted as the
// probes' code is (CodeBlock), its lines numbered as an error inside it names them
// ("<provided>", line 5); the numbers are not part of what a copy takes. Selections and copies
// are logged as the task description's are.
export default function ProvidedCode({ code }) {
  const { logTelemetryEvent } = useContext(TelemetryContext)

  const containerRef = useRef(null)

  useRegion(REGIONS.PROVIDED_CODE, containerRef)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    let lastSelection = ''

    const handleSelectionChange = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText && selectedText !== lastSelection) {
        lastSelection = selectedText
        logTelemetryEvent(PROVIDED_EVENTS.SELECT, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      } else if (!selectedText) {
        lastSelection = ''
      }
    }

    const handleCopy = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText) {
        logTelemetryEvent(PROVIDED_EVENTS.COPY, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      }
    }

    el.addEventListener('mouseup', handleSelectionChange)
    el.addEventListener('keyup', handleSelectionChange)
    el.addEventListener('copy', handleCopy)

    return () => {
      el.removeEventListener('mouseup', handleSelectionChange)
      el.removeEventListener('keyup', handleSelectionChange)
      el.removeEventListener('copy', handleCopy)
    }
  }, [logTelemetryEvent])

  return (
    <div ref={containerRef} className="ws-content-section">
      <h3 className="ws-section-title">Provided Code</h3>
      <p className="ws-provided-note">Read-only. It is loaded before your code every time you run it, so you can use it without copying it.</p>
      <div className="ws-provided-code" role="region" aria-label="Provided code (read-only)">
        <CodeBlock code={code} lang="python" lineNumbers />
      </div>
    </div>
  )
}
